import type { HTMLAttributes } from 'react';
import { motion, MotionConfig } from 'motion/react';
import { ShieldSlashIcon } from '@phosphor-icons/react';

import type { ChatUiMobilityOffer, MobilityOffersBlock } from '@voll-intelligence/genui-contract';
import { BLOCK_SHELL } from '../../../shared/ui/block_shell.js';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import {
  isHiddenInStack,
  stackStagger,
  stackedCardStyle,
  useStackedDeck,
  type StackGeometry,
} from '../../../shared/ui/stacked_deck.js';
import { formatKm, MobilityRow } from './mobility_row.js';
import '../../../shared/ui/flight_ui.css';
import './mobility_ui.css';

// Curva e duração do glide pilha↔lista (mesma sensação do deck de locação). O
// `motion` faz o FLIP da mudança de posição (prop `layout`); o stagger é o delay.
const GLIDE = { duration: 0.45, ease: [0.22, 0.61, 0.36, 1] as const };

/** Itens exibidos na variante colapsada: escolhido no topo + até 3 outros. */
const COLLAPSED_MAX = 4;

const SECTIONS: ChatUiMobilityOffer['section'][] = ['cooperative', 'car_driver'];

/** A pilha pós-escolha (#6323, rodada 3, Figma 6870:45352): cada carta de trás desce 4 e recua
 *  16 de cada lado — a da frente com a linha de 62, a pilha inteira com 70. */
const RIDE_STACK: StackGeometry = { peek: 4, inset: 16 };

/** A lista de linhas: o `@container` delas (a linha estreita lê a largura da LISTA, não da
 *  tela) e o fio de 1 px entre uma e outra. */
const ROWS = '@container m-0 list-none divide-y divide-border p-0';

function deckOffersFor(
  offers: ChatUiMobilityOffer[],
  selectedOfferId?: string,
): ChatUiMobilityOffer[] {
  const selected = offers.find((o) => o.offerId === selectedOfferId);
  const rest = offers.filter((o) => o.offerId !== selectedOfferId);
  return (selected ? [selected, ...rest] : offers).slice(0, COLLAPSED_MAX);
}

/** O gesto do deck: o deck inteiro abre e fecha como um botão; com um card só
 *  não há o que abrir e o deck é só apresentação. */
function deckToggleProps(
  toggleable: boolean,
  expanded: boolean,
  toggle: () => void,
): Pick<
  HTMLAttributes<HTMLDivElement>,
  'role' | 'tabIndex' | 'aria-expanded' | 'onClick' | 'onKeyDown'
> {
  if (!toggleable) return { role: 'presentation' };
  return {
    role: 'button',
    tabIndex: 0,
    'aria-expanded': expanded,
    onClick: toggle,
    onKeyDown: (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggle();
      }
    },
  };
}

type DistanceOverLimit = NonNullable<MobilityOffersBlock['distanceOverLimit']>;

/**
 * A política por distância (#6699, Figma 7517:78081): o limite é em km, então a corrida
 * inteira fica fora — uma linha no topo das opções, o escudo e o texto em `destructive`,
 * separada da lista por fio. Sem o limite configurado (a API ainda não o informa), diz só
 * que passou. Na pilha, segue na carta da corrida escolhida (`className` dá o respiro de cima
 * que a casca da carta não tem).
 */
function DistanceOverLimitLine({
  over,
  className = '',
}: {
  readonly over: DistanceOverLimit;
  readonly className?: string;
}) {
  const { t, locale } = useGenUiI18n();
  const km = formatKm(over.km, locale);
  return (
    <p
      className={`m-0 flex items-center gap-1.5 border-b border-border pb-2 text-detail text-destructive ${className}`}
    >
      <ShieldSlashIcon size={14} aria-hidden className="shrink-0" />
      {over.limitKm != null
        ? t('mobility.overLimit', { km, limit: formatKm(over.limitKm, locale) })
        : t('mobility.overLimitNoCap', { km })}
    </p>
  );
}

function MobilityDeck({
  offers,
  selectedOfferId,
  note,
  roundTrip,
  distanceOverLimit,
}: {
  readonly offers: ChatUiMobilityOffer[];
  readonly selectedOfferId?: string;
  readonly note?: string;
  readonly roundTrip: boolean;
  readonly distanceOverLimit?: DistanceOverLimit;
}) {
  const { renderMarkdown } = useGenUiConfig();
  const deckOffers = deckOffersFor(offers, selectedOfferId);
  const { expanded, stacked, listRef, toggle } = useStackedDeck(
    deckOffers.length,
    '.moi-deck-card',
    false,
    RIDE_STACK.peek,
  );
  const deckProps = deckToggleProps(deckOffers.length > 1, expanded, toggle);
  return (
    <MotionConfig reducedMotion="user">
      {/* A pilha da escolhida chega no lugar da lista: o leitor de tela anuncia a troca. */}
      <div
        className={`itin moi-collapsed moi-deck ${stacked ? 'is-stacked' : 'is-open'}`}
        aria-live="polite"
      >
        {note && <div className="itin-intro">{renderMarkdown ? renderMarkdown(note) : note}</div>}
        <div className="group/deck moi-deck-list" ref={listRef} {...deckProps}>
          {deckOffers.map((offer, index) => {
            const delay = stackStagger(index, deckOffers.length, expanded);
            const peek = stacked && index > 0;
            // A pilha inteira expande no clique: o hover pinta a casca do topo com o
            // `--sand-25` da linha, sem trocar a sombra (#6296).
            const hover = stacked && index === 0 ? ' group-hover/deck:bg-(--sand-25)' : '';
            const chosen = offer.offerId === selectedOfferId;
            // A política vale para a corrida: o aviso fica na carta dela depois da escolha.
            const overLimit =
              chosen && offer.section === 'cooperative' ? distanceOverLimit : undefined;
            return (
              <motion.div
                key={offer.offerId}
                className="moi-deck-card"
                style={stacked ? stackedCardStyle(index, deckOffers.length, RIDE_STACK) : undefined}
                aria-hidden={peek ? true : undefined}
                layout
                initial={false}
                animate={{ opacity: stacked && isHiddenInStack(index) ? 0 : 1 }}
                transition={{ layout: { ...GLIDE, delay }, opacity: { duration: 0.3, delay } }}
              >
                {/* Cada carta da pilha é uma casca com uma linha (o respiro de cima e de
                    baixo é o da própria linha); as de trás, a casca vazia na altura da do
                    topo. */}
                <div className={`${BLOCK_SHELL} py-0${hover}${peek ? ' moi-deck-blank' : ''}`}>
                  {!peek && overLimit ? (
                    <DistanceOverLimitLine over={overLimit} className="pt-2" />
                  ) : null}
                  {peek ? null : (
                    <ul className={ROWS}>
                      <MobilityRow
                        offer={offer}
                        position={index + 1}
                        selected={chosen}
                        roundTrip={roundTrip}
                        estimateNote={chosen}
                      />
                    </ul>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </MotionConfig>
  );
}

/**
 * Card de mobilidade no chat: as duas seções — corrida de cooperativa (valor
 * previsto) e carro com motorista (diária) — numa casca só (#6321), com as linhas
 * (`MobilityRow`) separadas por fio e o nome da seção como rótulo, na densidade dos
 * cards 1-Click (#6323: o bloco de 4 opções em 322 px). Pós-seleção o
 * agente re-emite o mesmo card com `variant: 'collapsed'` → deck empilhado
 * view-only com a escolha no topo, que o web-chat troca in-place (encolhe). Um
 * card congelado (`readOnly`) usa o mesmo deck. Com volta (`roundTrip`) a corrida
 * mostra o total das 2 corridas e a distância por trecho; fora do limite de km
 * (`distanceOverLimit`) o aviso vai no topo, para a corrida inteira (#6699).
 */
export function MobilityOffersList({
  offers,
  note,
  variant,
  selectedOfferId,
  readOnly = false,
  roundTrip = false,
  distanceOverLimit,
  onSelect,
}: {
  readonly offers: ChatUiMobilityOffer[];
  readonly note?: string;
  /** `collapsed` = lista compacta pós-seleção (view-only, escolhido no topo). */
  readonly variant?: 'collapsed';
  /** `offerId` escolhido — fixado no topo + destacado na variante collapsed. */
  readonly selectedOfferId?: string;
  /** Shell congelou o card (superseded / conversa encerrada) → deck colapsado. */
  readonly readOnly?: boolean;
  /** Ida e volta: a corrida custa as 2 corridas, a distância é por trecho. */
  readonly roundTrip?: boolean;
  /** A corrida passa do limite de km da empresa: aviso no topo das opções. */
  readonly distanceOverLimit?: DistanceOverLimit;
  readonly onSelect?: (offer: ChatUiMobilityOffer, position: number) => void;
}) {
  const { t } = useGenUiI18n();
  const { renderMarkdown } = useGenUiConfig();

  // View-only (pós-seleção ou lista superseded): deck empilhado.
  const deckLayout = variant === 'collapsed' || readOnly;

  if (deckLayout) {
    return (
      <MobilityDeck
        offers={offers}
        selectedOfferId={selectedOfferId}
        note={note}
        roundTrip={roundTrip}
        distanceOverLimit={distanceOverLimit}
      />
    );
  }

  // A posição (1-based) corre pelas seções na ordem em que aparecem.
  const groups: {
    section: ChatUiMobilityOffer['section'];
    rows: ChatUiMobilityOffer[];
    first: number;
  }[] = [];
  let next = 1;
  for (const section of SECTIONS) {
    const rows = offers.filter((o) => o.section === section);
    groups.push({ section, rows, first: next });
    next += rows.length;
  }
  return (
    <div className="itin moi-offers">
      {note && <div className="itin-intro">{renderMarkdown ? renderMarkdown(note) : note}</div>}
      <div className={BLOCK_SHELL}>
        {distanceOverLimit ? <DistanceOverLimitLine over={distanceOverLimit} /> : null}
        {groups.map(({ section, rows, first }) => {
          if (rows.length === 0) return null;
          return (
            <section key={section}>
              <h4 className="m-0 text-caption font-medium text-muted-foreground">
                {t(
                  section === 'cooperative' ? 'mobility.sectionRide' : 'mobility.sectionCarDriver',
                )}
              </h4>
              <ul className={ROWS}>
                {rows.map((o, index) => (
                  <MobilityRow
                    key={o.offerId}
                    offer={o}
                    position={first + index}
                    roundTrip={roundTrip}
                    onSelect={onSelect}
                  />
                ))}
              </ul>
              {/* A nota do valor previsto, uma vez, embaixo dos valores da corrida. */}
              {section === 'cooperative' ? (
                <p className="m-0 pb-1 text-right text-caption text-muted-foreground">
                  {t('mobility.estimateNote')}
                </p>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}
